/**
 * Challenge Hub view model for /product-insights/challenges.
 *
 * SEED runs two competitions with partner schools: the SEED Hackathon and the
 * SEED Business Challenge. Each edition (one per competition per year) has
 * partner schools; the B2B question is "what did each edition deliver for its
 * partner schools?". EMERGE proxies the read through
 * `GET /product-insights/challenges` (camelCase, `{data, meta}`); nothing is
 * stored.
 *
 * - The wire is read DEFENSIVELY into these view types (`toChallengesView`). A
 *   missing figure is `null` and renders an em dash, never `0`. `teams` and
 *   `finalists` are legitimately `null` on editions that had none.
 * - "Downstream" is correlation, not attribution: participants who LATER
 *   applied / were admitted / enrolled, anywhere and at a partner school.
 * - `intendedPgStartYear` is an object keyed by year plus `unknown`; it is
 *   turned into ordered rows here.
 * - A partner school links to EMERGE ONLY through the server's `company`
 *   enrichment. `upstreamCompanyId` is the SEED platform's company id, NOT
 *   `d_companies.id`; it is never used for a link.
 */

import type { ChallengeCompetition, ChallengeCompetitionFilter } from '@/lib/types';

export type { ChallengeCompetition, ChallengeCompetitionFilter };

export const COMPETITION_LABEL: Record<ChallengeCompetition, string> = {
  hackathon: 'Hackathon',
  business_challenge: 'Business Challenge',
};

export type ChallengeEditionSource = 'import' | 'comms_form';

export interface ChallengeCompanyLink {
  id: number;
  name: string;
}

export interface ChallengeDownstream {
  applied: number | null;
  admitted: number | null;
  enrolled: number | null;
  appliedAtPartner: number | null;
  admittedAtPartner: number | null;
  enrolledAtPartner: number | null;
}

export interface ChallengeOverallView {
  editions: number | null;
  participants: number | null;
  uniquePeople: number | null;
  linkedContacts: number | null;
  teams: number | null;
  finalists: number | null;
  winners: number | null;
  partnerSchools: number | null;
  downstream: ChallengeDownstream;
}

export interface ChallengePartnerSchool {
  key: string;
  schoolName: string;
  universityName: string | null;
  company: ChallengeCompanyLink | null;
}

export interface ChallengeCountRow {
  key: string;
  label: string;
  value: number;
  hint?: string;
}

export interface ChallengeEditionView {
  key: string;
  competition: ChallengeCompetition | null;
  editionYear: number | null;
  editionDate: string | null;
  title: string;
  participants: number | null;
  uniquePeople: number | null;
  linkedContacts: number | null;
  teams: number | null;
  finalists: number | null;
  winners: number | null;
  /** `null` = not reported (older API or unknown value). */
  source: ChallengeEditionSource | null;
  /** `true` only when the upstream says so; `null` = not reported. */
  registrationsOpen: boolean | null;
  /** Live registration count (`comms_form` editions); `null` on imported editions. */
  registrations: number | null;
  partnerSchools: ChallengePartnerSchool[];
  /** Named institutions (ranked), then "Other institutions" and "Not stated" when non-zero. */
  ugInstitutions: ChallengeCountRow[];
  /** Years ascending, then "Not stated" when non-zero. */
  pgStartYears: ChallengeCountRow[];
  downstream: ChallengeDownstream;
}

export interface ChallengeClientSchoolView {
  key: string;
  schoolName: string;
  universityName: string | null;
  company: ChallengeCompanyLink | null;
  partnerOf: string[];
  applied: number | null;
  admitted: number | null;
  enrolled: number | null;
}

export interface ChallengesView {
  overall: ChallengeOverallView;
  editions: ChallengeEditionView[];
  clientSchools: ChallengeClientSchoolView[];
  /** Static import date of the upstream data (`YYYY-MM-DD…`). */
  dataAsOf: string | null;
  /** The cycle the response is for; `null` = all editions. */
  cycleId: number | null;
  fetchedAt: string | null;
}

/* --- defensive readers ------------------------------------------------------ */

type Loose = Record<string, unknown>;

function asObject(value: unknown): Loose | null {
  return typeof value === 'object' && value !== null && !Array.isArray(value) ? (value as Loose) : null;
}

function asArray(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

function num(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() !== '' && Number.isFinite(Number(value))) {
    return Number(value);
  }
  return null;
}

function str(value: unknown): string | null {
  if (typeof value === 'string' && value.trim() !== '') return value;
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return null;
}

function first(obj: Loose, ...keys: string[]): unknown {
  for (const key of keys) if (obj[key] !== undefined) return obj[key];
  return undefined;
}

function competitionOf(value: unknown): ChallengeCompetition | null {
  return value === 'hackathon' || value === 'business_challenge' ? value : null;
}

function sourceOf(value: unknown): ChallengeEditionSource | null {
  return value === 'import' || value === 'comms_form' ? value : null;
}

function bool(value: unknown): boolean | null {
  return typeof value === 'boolean' ? value : null;
}

/**
 * The EMERGE company comes ONLY from the server's `company` (a staff-confirmed
 * school link). `upstreamCompanyId` is the SEED platform's id, never
 * `d_companies.id`, so it is never a link.
 */
function companyOf(obj: Loose, fallbackName: string): ChallengeCompanyLink | null {
  const company = asObject(obj.company);
  if (company === null) return null;
  const id = num(company.id);
  if (id === null) return null;
  return { id, name: str(company.name) ?? fallbackName };
}

function downstreamOf(value: unknown): ChallengeDownstream {
  const obj = asObject(value) ?? {};
  return {
    applied: num(obj.applied),
    admitted: num(obj.admitted),
    enrolled: num(obj.enrolled),
    appliedAtPartner: num(obj.appliedAtPartner),
    admittedAtPartner: num(obj.admittedAtPartner),
    enrolledAtPartner: num(obj.enrolledAtPartner),
  };
}

function overallOf(value: unknown): ChallengeOverallView {
  const obj = asObject(value) ?? {};
  return {
    editions: num(obj.editions),
    participants: num(obj.participants),
    uniquePeople: num(obj.uniquePeople),
    linkedContacts: num(obj.linkedContacts),
    teams: num(obj.teams),
    finalists: num(obj.finalists),
    winners: num(obj.winners),
    partnerSchools: num(obj.partnerSchools),
    downstream: downstreamOf(obj.downstream),
  };
}

function partnerSchoolsOf(value: unknown): ChallengePartnerSchool[] {
  const rows: ChallengePartnerSchool[] = [];
  asArray(value).forEach((entry, index) => {
    const obj = asObject(entry);
    if (obj === null) return;
    const schoolName = str(obj.schoolName) ?? str(obj.universityName) ?? '—';
    rows.push({
      key: str(obj.schoolId) ?? str(obj.upstreamCompanyId) ?? `${index}`,
      schoolName,
      universityName: str(obj.universityName),
      company: companyOf(obj, schoolName),
    });
  });
  return rows;
}

function ugInstitutionsOf(value: unknown): ChallengeCountRow[] {
  const obj = asObject(value);
  if (obj === null) return [];
  const rows: ChallengeCountRow[] = [];
  asArray(obj.top).forEach((entry, index) => {
    const item = asObject(entry);
    if (item === null) return;
    const label = str(first(item, 'ugInstitution', 'institution', 'name'));
    const count = num(item.participants);
    if (label === null || count === null) return;
    rows.push({ key: `top-${index}-${label}`, label, value: count });
  });
  rows.sort((a, b) => b.value - a.value || a.label.localeCompare(b.label));
  const others = num(obj.others);
  const otherInstitutions = num(obj.othersInstitutions);
  if (others !== null && others > 0) {
    rows.push({
      key: 'others',
      label: 'Other institutions',
      value: others,
      ...(otherInstitutions !== null && otherInstitutions > 0
        ? { hint: `${otherInstitutions.toLocaleString('en-GB')} institutions` }
        : {}),
    });
  }
  const notStated = num(obj.notStated);
  if (notStated !== null && notStated > 0) {
    rows.push({ key: 'not-stated', label: 'Not stated', value: notStated });
  }
  return rows;
}

function pgStartYearsOf(value: unknown): ChallengeCountRow[] {
  const obj = asObject(value);
  if (obj === null) return [];
  const years: ChallengeCountRow[] = [];
  let unknown: number | null = null;
  for (const [key, raw] of Object.entries(obj)) {
    const count = num(raw);
    if (count === null) continue;
    if (/^\d{4}$/.test(key)) years.push({ key, label: key, value: count });
    else unknown = (unknown ?? 0) + count;
  }
  years.sort((a, b) => a.label.localeCompare(b.label));
  if (unknown !== null && unknown > 0) years.push({ key: 'unknown', label: 'Not stated', value: unknown });
  return years;
}

function editionOf(value: unknown, index: number): ChallengeEditionView | null {
  const obj = asObject(value);
  if (obj === null) return null;
  const competition = competitionOf(obj.competition);
  const editionYear = num(obj.editionYear);
  const title =
    str(obj.title) ??
    [competition === null ? null : COMPETITION_LABEL[competition], editionYear]
      .filter((part) => part !== null)
      .join(' ');
  return {
    key: str(obj.key) ?? `edition-${index}`,
    competition,
    editionYear,
    editionDate: str(obj.editionDate),
    title: title === '' ? '—' : title,
    participants: num(obj.participants),
    uniquePeople: num(obj.uniquePeople),
    linkedContacts: num(obj.linkedContacts),
    teams: num(obj.teams),
    finalists: num(obj.finalists),
    winners: num(obj.winners),
    source: sourceOf(obj.source),
    registrationsOpen: bool(obj.registrationsOpen),
    registrations: num(obj.registrations),
    partnerSchools: partnerSchoolsOf(obj.partnerSchools),
    ugInstitutions: ugInstitutionsOf(obj.topUgInstitutions),
    pgStartYears: pgStartYearsOf(obj.intendedPgStartYear),
    downstream: downstreamOf(obj.downstream),
  };
}

function clientSchoolOf(value: unknown, index: number): ChallengeClientSchoolView | null {
  const obj = asObject(value);
  if (obj === null) return null;
  const schoolName = str(obj.schoolName) ?? str(obj.universityName) ?? '—';
  return {
    key: str(obj.schoolId) ?? str(obj.upstreamCompanyId) ?? `school-${index}`,
    schoolName,
    universityName: str(obj.universityName),
    company: companyOf(obj, schoolName),
    partnerOf: asArray(obj.partnerOf)
      .map((entry) => str(entry))
      .filter((entry): entry is string => entry !== null),
    applied: num(obj.applied),
    admitted: num(obj.admitted),
    enrolled: num(obj.enrolled),
  };
}

/* --- pure display helpers --------------------------------------------------- */

/**
 * The edition's headline head-count: a live registration form edition
 * (`comms_form` with a count) shows Registrations, every other edition
 * Participants.
 */
export function editionHeadcount(edition: ChallengeEditionView): { label: string; value: number | null } {
  if (edition.source === 'comms_form' && edition.registrations !== null) {
    return { label: 'Registrations', value: edition.registrations };
  }
  return { label: 'Participants', value: edition.participants };
}

/** Newest edition first; undated editions last. */
function byNewest(a: ChallengeEditionView, b: ChallengeEditionView): number {
  const left = a.editionDate ?? (a.editionYear === null ? null : String(a.editionYear));
  const right = b.editionDate ?? (b.editionYear === null ? null : String(b.editionYear));
  if (left === null && right === null) return a.title.localeCompare(b.title);
  if (left === null) return 1;
  if (right === null) return -1;
  return right.localeCompare(left) || a.title.localeCompare(b.title);
}

export function toChallengesView(raw: unknown): ChallengesView {
  const root = asObject(raw) ?? {};
  const data = asObject(root.data) ?? {};
  const meta = asObject(root.meta) ?? {};
  const editions = asArray(data.byChallenge)
    .map(editionOf)
    .filter((row): row is ChallengeEditionView => row !== null)
    .sort(byNewest);
  const clientSchools = asArray(data.byClientSchool)
    .map(clientSchoolOf)
    .filter((row): row is ChallengeClientSchoolView => row !== null);
  return {
    overall: overallOf(data.overall),
    editions,
    clientSchools,
    dataAsOf: str(meta.dataAsOf),
    cycleId: num(meta.cycleId),
    fetchedAt: str(meta.fetchedAt) ?? str(meta.generatedAt),
  };
}
